import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import type { Rule } from '@/domain/rules.types'
import { notification } from '@/notifications/notification-store'
import { libraryApi } from '@/services/library.service'
import { rulesApi } from '@/services/rules.service'

/** React-query hooks for the Rules Manager. */

export const useRules = () => useQuery({ queryKey: ['rules'], queryFn: rulesApi.list })

export const useRule = (id: string | null) =>
	useQuery({ queryKey: ['rule', id], queryFn: () => rulesApi.byId(id as string), enabled: !!id })

export const useCreateRule = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: rulesApi.create,
		onSuccess: () => qc.invalidateQueries({ queryKey: ['rules'] }),
	})
}

/** Edit rule properties, or archive it (patch `{ lifecycle: RuleLifecycle.RETIRED }`). */
export const useUpdateRule = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ id, patch }: { id: string; patch: Partial<Rule> }) => rulesApi.update(id, patch),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: ['rules'] })
			void qc.invalidateQueries({ queryKey: ['rule'] })
		},
		// Archiving closes its dialog on failure and has nowhere to put an inline
		// message, so an unreported error here is an invisible one.
		onError: (error) =>
			notification.error(
				isAxiosError(error) && error.response?.status === 403
					? 'Changing a rule is available to an administrator.'
					: 'Could not save this rule. Try again.',
			),
	})
}

/**
 * Classification axes + values for the create-rule axis picker, from the registry
 * facets mock (`/library/facets`). Single taxonomy source — no rules-specific axes.
 */
export const useFacets = () => useQuery({ queryKey: ['library', 'facets'], queryFn: libraryApi.facets })
